/* eslint-disable react/require-default-props */
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { FormControl, FormHelperText } from '@mui/material'
import { observer } from 'mobx-react'
import { ReactNode, ChangeEvent, useCallback, useMemo, memo } from 'react'
import FieldDataCollector from 'components/FieldDataCollector'
import useEmotionStyles from 'hooks/useEmotionStyles'
import { PathFormFieldProps } from 'interfaces/PathFormComponents.interface'
import { usePatchedField } from 'stores/utils/hooks/usePatchedField'
import { useStores } from 'stores/utils/hooks/useStores'
import WithFieldInteraction from 'utils/tracking/amplitude/WithFieldInteraction'
import WithIncreasedEmphasis from './components/WithIncreasedEmphasis'
import styles, { StyleProps } from './PathCheckboxYesNo.styles'

type Props = Omit<
  PathFormFieldProps<boolean>,
  'defaultValue' | 'tooltip' | 'onTooltip' | 'showTooltip'
> & {
  type?: boolean
  disabled?: boolean
  label?: ReactNode
  increaseEmphasis?: boolean
  alignOnTop?: boolean
}

const YesNoCheckbox = observer(
  ({ path, label, increaseEmphasis = false, alignOnTop, disabled }: Props) => {
    const { input, error } = usePatchedField(path, {
      type: 'checkbox'
    })

    const {
      traversal: { removeErrorsForPath }
    } = useStores()

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        input.onChange(e)
        input.onBlur()
        removeErrorsForPath(path)
      },
      [input, removeErrorsForPath, path]
    )

    const isEmphasized = useMemo(
      () => increaseEmphasis && !input.checked,
      [increaseEmphasis, input.checked]
    )

    const hasError = !!error
    const { checkbox, formLabel } = useEmotionStyles<
      ReturnType<typeof styles>,
      StyleProps
    >(styles, {
      hasError,
      increaseEmphasis,
      isChecked: !!input.checked,
      alignOnTop: alignOnTop || false
    })

    return (
      <FormControl error={hasError}>
        <FormControlLabel
          className={formLabel}
          control={
            <WithIncreasedEmphasis display={isEmphasized}>
              <WithFieldInteraction
                trackFocusEvents={false}
                trackOnChange
                path={path}
              >
                <Checkbox
                  className={checkbox}
                  checked={input.checked}
                  name={input.name}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  value={input.value}
                  disabled={disabled}
                  onChange={handleChange}
                  color="primary"
                />
              </WithFieldInteraction>
            </WithIncreasedEmphasis>
          }
          label={label}
        />
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    )
  }
)

const PathCheckboxYesNo = (props: Props) => {
  const {
    path,
    labelPath,
    label,
    increaseEmphasis,
    alignOnTop,
    type,
    disabled
  } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <YesNoCheckbox
        path={path}
        labelPath={labelPath}
        label={label}
        increaseEmphasis={increaseEmphasis}
        alignOnTop={alignOnTop}
        type={type}
        disabled={disabled}
      />
    </FieldDataCollector>
  )
}

export default memo(PathCheckboxYesNo)
